/**
 * Measurement Planner - Multilingual Version (DE/PL)
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'hafa_measurement_planner';
  const WIDGET_STORAGE_KEY = 'hafa_checklist_data';

  // ========================================
  // ÜBERSETZUNGEN - Zentrale Verwaltung
  // ========================================
  const i18n = {
    de: {
      // Progress & Status
      stairsCompleted: '{count}/{total} Stufen erledigt',
      
      // Options summary
      withSetzstufen: 'mit Setzstufen',
      withLED: 'mit LED',
      withWangen: 'mit Wangen',
      withBoden: 'mit Podestboden',
      withKappen: 'mit Stufenkappen',
      
      // Stair entry headers
      width: 'Breite',
      depth: 'Tiefe',
      height: 'Höhe',
      note: 'Notiz',
      notePlaceholder: 'Notiz',
      mmPlaceholder: 'mm',
      
      // Meter/sqm input
      totalLengthMm: 'Gesamtlänge in mm',
      totalSqm: 'Fläche in m²',
      exampleMm: 'z.B. 8500',
      exampleSqm: 'z.B. 4.5',
      
      // Messages
      listSaved: 'Liste gespeichert!',
      goToProduct: 'Zur Auswahl',
      afterNeed: 'nach Bedarf'
    },
    pl: {
      // Progress & Status
      stairsCompleted: '{count}/{total} stopni ukończonych',
      
      // Options summary
      withSetzstufen: 'z podstopnicami',
      withLED: 'z LED',
      withWangen: 'z okładziną policzków',
      withBoden: 'z podłogą podestu',
      withKappen: 'z nakładkami',
      
      // Stair entry headers
      width: 'Szerokość',
      depth: 'Głębokość',
      height: 'Wysokość',
      note: 'Notatka',
      notePlaceholder: 'Notatka',
      mmPlaceholder: 'mm',
      
      // Meter/sqm input
      totalLengthMm: 'Całkowita długość w mm',
      totalSqm: 'Powierzchnia w m²',
      exampleMm: 'np. 8500',
      exampleSqm: 'np. 4.5',
      
      // Messages
      listSaved: 'Lista zapisana!',
      goToProduct: 'Aby wybrać',
      afterNeed: 'według potrzeb'
    }
  };

  let currentLang = 'de';
  
  function detectLanguage() {
    // Check for data-lang attribute on planner
    const planner = document.querySelector('.measurement-planner');
    if (planner?.dataset.lang) {
      return planner.dataset.lang;
    }
    // Fallback: check html lang attribute
    const htmlLang = document.documentElement.lang?.substring(0, 2).toLowerCase();
    return htmlLang === 'pl' ? 'pl' : 'de';
  }
  
  function t(key, replacements = {}) {
    let text = i18n[currentLang]?.[key] || i18n.de[key] || key;
    // Replace placeholders like {count}, {total}
    Object.keys(replacements).forEach(k => {
      text = text.replace(`{${k}}`, replacements[k]);
    });
    return text;
  }

  let config = {};
  let state = {
    stairCount: 13,
    options: { setzstufen: true, led: false, wangen: false, boden: false, kappen: false },
    checklist: [],
    itemMeasurements: {},
    checkedStairs: {},
    expandedItems: [],
    manualValues: {}
  };

  function init() {
    currentLang = detectLanguage();
    loadConfig();
    loadState();
    bindEvents();
    updateUI();
  }

  function loadConfig() {
    const configEl = document.getElementById('checklist-config');
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('Config parse error:', e);
        config = { items: [], translations: {} };
      }
    }
  }

  function loadState() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        state = { ...state, ...parsed };
        if (!state.checkedStairs) state.checkedStairs = {};
        if (!state.manualValues) state.manualValues = {};
        if (!state.itemMeasurements) {
          state.itemMeasurements = {};
          if (parsed.stairMeasurements) {
            state.itemMeasurements['renovierungsstufen'] = parsed.stairMeasurements;
          }
        }
      } catch (e) { console.error('State load error:', e); }
    }
    
    const stairInput = document.getElementById('stair-count');
    if (stairInput) stairInput.value = state.stairCount;
    
    ['setzstufen', 'led', 'wangen', 'boden', 'kappen'].forEach(opt => {
      const el = document.getElementById(`option-${opt}`);
      if (el) el.checked = state.options[opt];
    });
  }

  function saveState() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    saveWidgetData();
  }

  function saveWidgetData() {
    if (!config.items) return;
    
    // Helper function to check item conditions (same as updateChecklist)
    const checkCondition = (item) => {
      if (!item.condition) return true;
      if (item.condition === 'boden_or_wangen') {
        return state.options.boden || state.options.wangen;
      }
      return state.options[item.condition];
    };
    
    const widgetData = {
      stairCount: state.stairCount,
      options: state.options,
      checklist: state.checklist,
      checkedStairs: state.checkedStairs,
      manualValues: state.manualValues,
      itemMeasurements: state.itemMeasurements,
      items: config.items
        .filter(item => checkCondition(item))
        .map(item => {
          const hasStairDetails = item.hasMeasurements && item.calculation === 'stairs';
          return {
            id: item.id,
            // Note: We only save the ID, the widget will translate based on ID
            quantity: calculateQuantity(item),
            checked: hasStairDetails ? areAllStairsChecked(item.id) : state.checklist.includes(item.id),
            link: item.link || '',
            hasStairs: hasStairDetails,
            checkedStairs: state.checkedStairs[item.id] || [],
            totalStairs: hasStairDetails ? state.stairCount : 0
          };
        })
    };
    localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(widgetData));
  }

  function bindEvents() {
    document.querySelectorAll('.mp-stair-counter__btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const action = e.currentTarget.dataset.action;
        if (action === 'increase') updateStairCount(1);
        if (action === 'decrease') updateStairCount(-1);
      });
    });

    const stairInput = document.getElementById('stair-count');
    if (stairInput) {
      stairInput.addEventListener('change', handleStairInputChange);
      stairInput.addEventListener('blur', handleStairInputChange);
    }

    ['setzstufen', 'led', 'wangen', 'boden', 'kappen'].forEach(option => {
      const el = document.getElementById(`option-${option}`);
      if (el) {
        el.addEventListener('change', (e) => {
          state.options[option] = e.target.checked;
          updateUI();
          saveState();
        });
      }
    });

    const printBtn = document.getElementById('btn-print-sheet');
    if (printBtn) {
      printBtn.addEventListener('click', handlePrint);
    }

    const saveBtn = document.getElementById('btn-save-list');
    if (saveBtn) {
      saveBtn.addEventListener('click', () => {
        saveState();
        showToast(t('listSaved'));
      });
    }

    document.querySelectorAll('a[href^="#"]').forEach(link => {
      link.addEventListener('click', (e) => {
        const target = document.querySelector(link.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  function updateStairCount(delta) {
    const newValue = state.stairCount + delta;
    if (newValue >= 1 && newValue <= 50) {
      state.stairCount = newValue;
      document.getElementById('stair-count').value = newValue;
      updateUI();
      saveState();
    }
  }

  function handleStairInputChange(e) {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 1) value = 1;
    if (value > 50) value = 50;
    state.stairCount = value;
    e.target.value = value;
    updateUI();
    saveState();
  }

  function calculateQuantity(item) {
    switch (item.calculation) {
      case 'stairs':
        return state.stairCount;
      case 'per_stair':
        return Math.ceil(state.stairCount * (item.factor || 1));
      case 'divide':
        return Math.ceil(state.stairCount / (item.divisor || 1));
      case 'fixed':
        return item.amount || 1;
      case 'editable':
        return state.manualValues[item.id] !== undefined ? state.manualValues[item.id] : (item.amount || 1);
      case 'manual':
        return state.manualValues[item.id] || '–';
      case 'meter_sets':
        const meters = state.manualValues[item.id] || 0;
        if (!meters || meters <= 0) return '–';
        const setSize = item.setSize || 5600;
        return Math.ceil(meters / setSize);
      case 'sqm_packages':
        const sqm = state.manualValues[item.id] || 0;
        if (!sqm || sqm <= 0) return '–';
        const packageSize = item.packageSize || 2.3;
        return Math.ceil(sqm / packageSize);
      case 'info_only':
        return '–';
      default:
        return state.stairCount;
    }
  }

  function getCheckedStairsCount(itemId) {
    return (state.checkedStairs[itemId] || []).length;
  }

  function areAllStairsChecked(itemId) {
    return getCheckedStairsCount(itemId) >= state.stairCount;
  }

  function updateUI() {
    updateSummary();
    updateChecklist();
    updateMeasurementSheet();
  }

  function updateSummary() {
    const summaryStairs = document.getElementById('summary-stairs');
    if (summaryStairs) summaryStairs.textContent = state.stairCount;

    const summaryOptions = document.getElementById('summary-options');
    if (summaryOptions) {
      const opts = [];
      if (state.options.setzstufen) opts.push(t('withSetzstufen'));
      if (state.options.led) opts.push(t('withLED'));
      if (state.options.wangen) opts.push(t('withWangen'));
      if (state.options.boden) opts.push(t('withBoden'));
      if (state.options.kappen) opts.push(t('withKappen'));
      summaryOptions.textContent = opts.length ? `(${opts.join(', ')})` : '';
    }
  }

  function updateChecklist() {
    const container = document.getElementById('shopping-checklist');
    if (!container || !config.items) return;

    // Helper function to check item conditions
    const checkCondition = (item) => {
      if (!item.condition) return true;
      if (item.condition === 'boden_or_wangen') {
        return state.options.boden || state.options.wangen;
      }
      return state.options[item.condition];
    };

    const html = config.items
      .filter(item => checkCondition(item))
      .map(item => {
        const quantity = calculateQuantity(item);
        const isExpanded = state.expandedItems.includes(item.id);
        const hasStairDetails = item.hasMeasurements && item.calculation === 'stairs';
        const isMeterSets = item.calculation === 'meter_sets';
        const isSqmPackages = item.calculation === 'sqm_packages';
        const isEditable = item.calculation === 'editable';
        const isInfoOnly = item.calculation === 'info_only';
        const checkedCount = hasStairDetails ? getCheckedStairsCount(item.id) : 0;
        const allStairsChecked = hasStairDetails ? areAllStairsChecked(item.id) : false;
        const isMainChecked = hasStairDetails ? allStairsChecked : state.checklist.includes(item.id);

        let extraInputHtml = '';
        if (isMeterSets) {
          const meterValue = state.manualValues[item.id] || '';
          const setSize = item.setSize || 5600;
          const sets = meterValue ? Math.ceil(meterValue / setSize) : 0;
          extraInputHtml = `
            <div class="mp-checklist-item__meter-input">
              <label>${t('totalLengthMm')}:</label>
              <input type="number" class="mp-meter-input" data-item-id="${item.id}" data-calc-type="meter_sets" value="${meterValue}" placeholder="${t('exampleMm')}">
              <span class="mp-meter-result">${sets > 0 ? `= ${sets} ${item.unit}` : ''}</span>
            </div>
          `;
        }
        
        if (isSqmPackages) {
          const sqmValue = state.manualValues[item.id] || '';
          const packageSize = item.packageSize || 2.3;
          const packages = sqmValue ? Math.ceil(sqmValue / packageSize) : 0;
          extraInputHtml = `
            <div class="mp-checklist-item__meter-input">
              <label>${t('totalSqm')}:</label>
              <input type="number" step="0.1" class="mp-meter-input" data-item-id="${item.id}" data-calc-type="sqm_packages" value="${sqmValue}" placeholder="${t('exampleSqm')}">
              <span class="mp-meter-result">${packages > 0 ? `= ${packages} ${item.unit}` : ''}</span>
            </div>
          `;
        }

        let quantityHtml = '';
        if (isEditable) {
          quantityHtml = `
            <div class="mp-checklist-item__editable-qty">
              <button type="button" class="mp-qty-btn mp-qty-btn--minus" data-item-id="${item.id}" data-action="decrease">−</button>
              <span class="mp-qty-value" data-item-id="${item.id}">${quantity}</span>
              <button type="button" class="mp-qty-btn mp-qty-btn--plus" data-item-id="${item.id}" data-action="increase">+</button>
              <span class="mp-qty-unit">${item.unit}</span>
            </div>
          `;
        } else if (isInfoOnly) {
          quantityHtml = `<span class="mp-checklist-item__quantity mp-checklist-item__quantity--info">${t('afterNeed')}</span>`;
        } else {
          quantityHtml = `<span class="mp-checklist-item__quantity">${quantity} ${item.unit}</span>`;
        }

        // Determine column header for 2nd measurement field
        const field2Header = item.id === 'setzstufen' ? t('height') : t('depth');

        return `
          <div class="mp-checklist-item ${isMainChecked ? 'mp-checklist-item--checked' : ''}" data-item-id="${item.id}" data-has-stairs="${hasStairDetails}">
            <div class="mp-checklist-item__main">
              <input type="checkbox" class="mp-checklist-item__checkbox" id="check-${item.id}" ${isMainChecked ? 'checked' : ''} data-is-stair-item="${hasStairDetails}">
              <div class="mp-checklist-item__info">
                <p class="mp-checklist-item__name">${item.name}</p>
                ${item.hint ? `<p class="mp-checklist-item__hint">${item.hint}</p>` : ''}
                ${hasStairDetails ? `<p class="mp-checklist-item__progress">${t('stairsCompleted', { count: checkedCount, total: state.stairCount })}</p>` : ''}
              </div>
              ${quantityHtml}
              ${hasStairDetails ? `<button type="button" class="mp-checklist-item__toggle ${isExpanded ? 'mp-checklist-item__toggle--open' : ''}" data-toggle="${item.id}"><svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>` : ''}
              ${item.link ? `<a href="${item.link}" class="mp-checklist-item__link">${t('goToProduct')}<svg viewBox="0 0 20 20" fill="none" width="16" height="16"><path d="M7 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></a>` : ''}
            </div>
            ${extraInputHtml}
            ${hasStairDetails ? `
              <div class="mp-checklist-item__stairs ${isExpanded ? 'mp-checklist-item__stairs--open' : ''}" id="stairs-${item.id}">
                <div class="mp-stairs-header"><span></span><span>#</span><span>${t('width')}</span><span>${field2Header}</span><span>${t('note')}</span></div>
                <div class="mp-stairs-grid">${generateStairEntries(item.id)}</div>
              </div>
            ` : ''}
          </div>
        `;
      }).join('');

    container.innerHTML = html;
    bindChecklistEvents();
  }

  function generateStairEntries(itemId) {
    const checkedStairs = state.checkedStairs[itemId] || [];
    const itemMeasurements = state.itemMeasurements[itemId] || {};
    const isSetzstufen = itemId === 'setzstufen';
    const field2Name = isSetzstufen ? 'height' : 'depth';
    
    let entries = '';
    for (let i = 1; i <= state.stairCount; i++) {
      const m = itemMeasurements[i] || {};
      const isStairChecked = checkedStairs.includes(i);
      entries += `
        <div class="mp-stair-entry ${isStairChecked ? 'mp-stair-entry--checked' : ''}" data-stair="${i}" data-item="${itemId}">
          <input type="checkbox" class="mp-stair-entry__checkbox" data-stair-check="${i}" data-item-id="${itemId}" ${isStairChecked ? 'checked' : ''}>
          <span class="mp-stair-entry__number">${i}</span>
          <input type="text" placeholder="${t('mmPlaceholder')}" value="${m.width || ''}" data-stair="${i}" data-item="${itemId}" data-field="width" class="mp-stair-entry__input">
          <input type="text" placeholder="${t('mmPlaceholder')}" value="${m[field2Name] || ''}" data-stair="${i}" data-item="${itemId}" data-field="${field2Name}" class="mp-stair-entry__input">
          <input type="text" placeholder="${t('notePlaceholder')}" value="${m.notes || ''}" data-stair="${i}" data-item="${itemId}" data-field="notes" class="mp-stair-entry__input mp-stair-entry__input--notes">
        </div>
      `;
    }
    return entries;
  }

  function bindChecklistEvents() {
    // Main checkboxes
    document.querySelectorAll('.mp-checklist-item__checkbox').forEach(cb => {
      cb.addEventListener('change', (e) => {
        const itemEl = e.target.closest('.mp-checklist-item');
        const itemId = itemEl.dataset.itemId;
        const hasStairs = itemEl.dataset.hasStairs === 'true';
        const isChecked = e.target.checked;
        
        if (hasStairs) {
          state.checkedStairs[itemId] = isChecked ? Array.from({length: state.stairCount}, (_, i) => i + 1) : [];
          const stairsContainer = document.getElementById(`stairs-${itemId}`);
          if (stairsContainer) {
            stairsContainer.querySelectorAll('.mp-stair-entry').forEach(entry => {
              entry.querySelector('.mp-stair-entry__checkbox').checked = isChecked;
              entry.classList.toggle('mp-stair-entry--checked', isChecked);
            });
          }
          updateProgressDisplay(itemId);
        } else {
          if (isChecked) {
            if (!state.checklist.includes(itemId)) state.checklist.push(itemId);
          } else {
            state.checklist = state.checklist.filter(id => id !== itemId);
          }
        }
        itemEl.classList.toggle('mp-checklist-item--checked', isChecked);
        saveState();
      });
    });

    // Toggle buttons
    document.querySelectorAll('.mp-checklist-item__toggle').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const itemId = e.currentTarget.dataset.toggle;
        const stairsEl = document.getElementById(`stairs-${itemId}`);
        const isOpen = state.expandedItems.includes(itemId);
        if (isOpen) {
          state.expandedItems = state.expandedItems.filter(id => id !== itemId);
          stairsEl.classList.remove('mp-checklist-item__stairs--open');
          e.currentTarget.classList.remove('mp-checklist-item__toggle--open');
        } else {
          state.expandedItems.push(itemId);
          stairsEl.classList.add('mp-checklist-item__stairs--open');
          e.currentTarget.classList.add('mp-checklist-item__toggle--open');
        }
        saveState();
      });
    });

    // Individual stair checkboxes
    document.querySelectorAll('.mp-stair-entry__checkbox').forEach(cb => {
      cb.addEventListener('change', (e) => {
        const stairNum = parseInt(e.target.dataset.stairCheck, 10);
        const itemId = e.target.dataset.itemId;
        const isChecked = e.target.checked;
        
        if (!state.checkedStairs[itemId]) state.checkedStairs[itemId] = [];
        if (isChecked) {
          if (!state.checkedStairs[itemId].includes(stairNum)) state.checkedStairs[itemId].push(stairNum);
        } else {
          state.checkedStairs[itemId] = state.checkedStairs[itemId].filter(n => n !== stairNum);
        }
        
        e.target.closest('.mp-stair-entry').classList.toggle('mp-stair-entry--checked', isChecked);
        
        const mainCheckbox = document.querySelector(`#check-${itemId}`);
        const allChecked = areAllStairsChecked(itemId);
        if (mainCheckbox) {
          mainCheckbox.checked = allChecked;
          mainCheckbox.closest('.mp-checklist-item').classList.toggle('mp-checklist-item--checked', allChecked);
        }
        updateProgressDisplay(itemId);
        saveState();
      });
    });

    // Stair measurement inputs
    document.querySelectorAll('.mp-stair-entry__input').forEach(input => {
      input.addEventListener('change', (e) => {
        const stairNum = parseInt(e.target.dataset.stair, 10);
        const itemId = e.target.dataset.item;
        const field = e.target.dataset.field;
        
        if (!state.itemMeasurements[itemId]) state.itemMeasurements[itemId] = {};
        if (!state.itemMeasurements[itemId][stairNum]) state.itemMeasurements[itemId][stairNum] = {};
        state.itemMeasurements[itemId][stairNum][field] = e.target.value;
        saveState();
      });
    });

    // Meter/sqm input for wangen and boden
    document.querySelectorAll('.mp-meter-input').forEach(input => {
      input.addEventListener('change', (e) => {
        const itemId = e.target.dataset.itemId;
        const calcType = e.target.dataset.calcType;
        const value = parseFloat(e.target.value) || 0;
        state.manualValues[itemId] = value;
        
        const item = config.items.find(i => i.id === itemId);
        if (item) {
          let result = 0;
          if (calcType === 'meter_sets') {
            const setSize = item.setSize || 5600;
            result = value > 0 ? Math.ceil(value / setSize) : 0;
          } else if (calcType === 'sqm_packages') {
            const packageSize = item.packageSize || 2.3;
            result = value > 0 ? Math.ceil(value / packageSize) : 0;
          }
          
          const resultEl = e.target.parentElement.querySelector('.mp-meter-result');
          if (resultEl) resultEl.textContent = result > 0 ? `= ${result} ${item.unit}` : '';
          
          const qtyEl = document.querySelector(`[data-item-id="${itemId}"] .mp-checklist-item__quantity`);
          if (qtyEl) qtyEl.textContent = `${result > 0 ? result : '–'} ${item.unit}`;
        }
        saveState();
      });
    });

    // Editable quantity +/- buttons
    document.querySelectorAll('.mp-qty-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const itemId = e.target.dataset.itemId;
        const action = e.target.dataset.action;
        const item = config.items.find(i => i.id === itemId);
        
        let currentValue = state.manualValues[itemId] !== undefined ? state.manualValues[itemId] : (item?.amount || 1);
        
        if (action === 'increase') {
          currentValue++;
        } else if (action === 'decrease' && currentValue > 0) {
          currentValue--;
        }
        
        state.manualValues[itemId] = currentValue;
        
        const valueEl = document.querySelector(`.mp-qty-value[data-item-id="${itemId}"]`);
        if (valueEl) valueEl.textContent = currentValue;
        
        saveState();
      });
    });
  }

  function updateProgressDisplay(itemId) {
    const progressEl = document.querySelector(`[data-item-id="${itemId}"] .mp-checklist-item__progress`);
    if (progressEl) {
      progressEl.textContent = t('stairsCompleted', { 
        count: getCheckedStairsCount(itemId), 
        total: state.stairCount 
      });
    }
  }

  function updateMeasurementSheet() {
    const tbody = document.getElementById('measurement-sheet-body');
    if (!tbody) return;
    
    const measurements = state.itemMeasurements['renovierungsstufen'] || {};
    let rows = '';
    for (let i = 1; i <= state.stairCount; i++) {
      const m = measurements[i] || {};
      rows += `<tr><td>${i}</td><td>${m.width || ''}</td><td>${m.depth || ''}</td><td>${m.notes || ''}</td></tr>`;
    }
    tbody.innerHTML = rows;
    
    // Show/hide wangen section
    const wangenSection = document.getElementById('wangen-print-section');
    if (wangenSection) {
      wangenSection.style.display = state.options.wangen ? 'block' : 'none';
    }
    
    // Show/hide boden section
    const bodenSection = document.getElementById('boden-print-section');
    if (bodenSection) {
      bodenSection.style.display = state.options.boden ? 'block' : 'none';
    }
  }

  function handlePrint() {
    updateMeasurementSheet();
    window.print();
  }

  function showToast(message) {
    const toast = document.createElement('div');
    toast.className = 'mp-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.HafaMeasurementPlanner = { getState: () => state, saveState };
})();
